import 'react-swipeable-list/dist/styles.css'

import { BookMarked, Check, Copy } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SwipeableList, SwipeableListItem, SwipeAction, TrailingActions, Type as ListType } from 'react-swipeable-list'

import { SegmentResponse, TranscribeResponse } from '@/api/type'
import { Button } from '@/components/ui/button'
import { SILENCE_TEXT } from '@/lib/constants'
import { formatTime } from '@/lib/time-format'
import { useFlashcardStore } from '@/store/useFlashcardStore'
import { SHOW_TEXT_MODES, ShowTextMode } from '@/types/playback'

import { useSegmentEditor } from './hooks/useSegmentEditor'
import { TranslateButton } from './translate-button'

const CopyButton = ({ text, onClick }: { text: string; onClick?: (e: React.MouseEvent) => void }) => {
  const [copied, setCopied] = useState(false)
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onClick?.(e)
      void navigator.clipboard.writeText(text).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
    },
    [text, onClick],
  )
  return (
    <button
      onClick={handleClick}
      className="flex items-center justify-center p-0! w-5 h-5 rounded transition-colors hover:bg-accent"
      title="コピー"
    >
      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-muted-foreground" />}
    </button>
  )
}

type TranscriptionDisplayProps = {
  transcription: TranscribeResponse | null
  setTranscription: (value: TranscribeResponse | null) => void
  showText: ShowTextMode
  currentSegmentIndex: number | null
  currentWordTime: number
  isTranslatingFullText: boolean
  showFullTextTranslation: boolean
  fullTextTranslation: string | null
  handleTranslateFullText: () => void
  handleSegmentClick: (segment: SegmentResponse, index: number) => void
  isPausedByAuto: boolean
  autoPauseTotal: number
  autoPauseProgress: number
  translatingSegmentIndex: number | null
  handleTranslateSegment: (index: number, e: React.MouseEvent) => void
  handleWordClick: (
    word: { word: string; start: number; end: number },
    segmentIndex: number,
    e: React.MouseEvent,
  ) => void
  episodeId: number
  onFlashcardRegister: (segmentIndex: number) => void
}

/**
 * Transcription表示コンポーネント
 * TEXT_ONLYモードとFULLモードの2つの表示形式をサポート
 */
export const TranscriptionDisplay = ({
  transcription,
  setTranscription,
  showText,
  currentSegmentIndex,
  currentWordTime,
  isTranslatingFullText,
  showFullTextTranslation,
  fullTextTranslation,
  handleTranslateFullText,
  handleSegmentClick,
  isPausedByAuto,
  autoPauseTotal,
  autoPauseProgress,
  translatingSegmentIndex,
  handleTranslateSegment,
  handleWordClick,
  episodeId,
  onFlashcardRegister,
}: TranscriptionDisplayProps) => {
  const segmentRefs = useRef<Map<number, HTMLDivElement | null>>(new Map())
  const [isEditingWord, setIsEditingWord] = useState(false)
  const { isSegmentRegistered } = useFlashcardStore()

  // カスタムフック: セグメント編集機能
  const {
    longPressedWord,
    setLongPressedWord,
    editingWordText,
    setEditingWordText,
    mergeWithNext,
    handleWordTouchStart,
    handleWordTouchEnd,
    handleEditWord,
    toggleSkipSegment,
    handleSplitSegment,
  } = useSegmentEditor({ transcription, setTranscription })

  // 現在のセグメントが表示範囲外に出たらスクロール
  useEffect(() => {
    if (currentSegmentIndex !== null) {
      const element = segmentRefs.current.get(currentSegmentIndex)
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }
  }, [currentSegmentIndex])

  // 単語編集ダイアログが開いたら編集モードをリセット
  useEffect(() => {
    if (longPressedWord) {
      setIsEditingWord(false)
    }
  }, [longPressedWord])

  const allWordsLength = useMemo(() => {
    if (!transcription) return 0
    return transcription.segments
      .filter((segment) => !(segment.text === SILENCE_TEXT || segment.skipped))
      .flatMap((segment) => segment.words || []).length
  }, [transcription])

  if (!transcription) return null

  if (showText === SHOW_TEXT_MODES.TEXT_ONLY) {
    return (
      <div className="bg-card rounded-lg shadow-md relative">
        {/* コピー・翻訳ボタン（右上） */}
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
          <CopyButton
            text={transcription.segments
              .filter((s) => !(s.text === SILENCE_TEXT || s.skipped))
              .map((s) => s.text)
              .join(' ')}
          />
          <TranslateButton
            isTranslating={isTranslatingFullText}
            onClick={handleTranslateFullText}
            isActive={showFullTextTranslation}
          />
        </div>
        <div className="space-y-2">
          {transcription.segments
            .filter((segment) => !(segment.text === SILENCE_TEXT || segment.skipped))
            .map((segment, index) => {
              const originalIndex = transcription.segments.indexOf(segment)
              return (
                <div
                  key={index}
                  className={`rounded-md cursor-pointer transition-colors ${
                    currentSegmentIndex === originalIndex ? 'bg-primary/10 border-primary' : 'hover:bg-accent'
                  }`}
                  onClick={() => handleSegmentClick(segment, originalIndex)}
                >
                  <p className="text-base leading-relaxed">
                    {segment.words && segment.words.length > 0
                      ? segment.words.map((word, wordIndex) => {
                          const nextWordStart = segment.words[wordIndex + 1]?.start ?? segment.end
                          const isCurrentWord = currentWordTime >= word.start && currentWordTime < nextWordStart
                          return (
                            <span key={wordIndex} className={isCurrentWord ? 'text-red-500' : ''}>
                              {word.word}
                            </span>
                          )
                        })
                      : segment.text}
                  </p>
                </div>
              )
            })}
        </div>

        {/* 全文翻訳結果 */}
        {fullTextTranslation && showFullTextTranslation && (
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">日本語訳</h3>
            <p className="text-base leading-relaxed">{fullTextTranslation}</p>
          </div>
        )}
      </div>
    )
  }

  // TrailingActionsを定義（結合ボタン）
  const trailingActions = (index: number) => {
    // 最後のセグメントの場合はundefinedを返す
    if (!transcription || index >= transcription.segments.length - 1) {
      return undefined
    }

    return (
      <TrailingActions>
        <SwipeAction destructive={false} onClick={() => mergeWithNext(index)}>
          <div className="flex items-center justify-center h-full px-6 bg-destructive text-destructive-foreground font-semibold rounded-e-md">
            下と結合
          </div>
        </SwipeAction>
      </TrailingActions>
    )
  }

  return (
    <div className="bg-card rounded-lg shadow-md">
      <p className="text-sm text-muted-foreground">
        language: {transcription.language} | {allWordsLength} words
      </p>
      <SwipeableList type={ListType.IOS} fullSwipe={false}>
        {transcription.segments.map((segment, index) => (
          <SwipeableListItem
            key={`${segment.start}-${segment.end}`}
            trailingActions={trailingActions(index)}
            threshold={0.3}
          >
            <div
              ref={(el) => {
                segmentRefs.current.set(index, el)
              }}
              className="w-full rounded-md"
            >
              {/* セグメント本体 */}
              <div
                onClick={() => handleSegmentClick(segment, index)}
                className={`p-2 rounded-md cursor-pointer transition-all ${
                  segment.skipped
                    ? 'bg-muted opacity-50 cursor-not-allowed'
                    : currentSegmentIndex === index
                      ? 'bg-blue-900/30 dark:bg-blue-900/40 text-foreground shadow-lg border-2 border-blue-500/50'
                      : 'bg-background hover:bg-accent'
                }`}
                style={{
                  ...(isPausedByAuto && currentSegmentIndex === index && autoPauseTotal > 0
                    ? {
                        background: `linear-gradient(to right, rgb(34 197 94 / 0.3) ${(autoPauseProgress / autoPauseTotal) * 100}%, transparent ${(autoPauseProgress / autoPauseTotal) * 100}%)`,
                      }
                    : {}),
                }}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs font-mono text-muted-foreground">
                    {formatTime(segment.start)} - {formatTime(segment.end)}
                  </span>
                  <div className="flex items-center gap-2">
                    {/* コピー・翻訳・単語帳ボタン */}
                    {segment.text !== SILENCE_TEXT && (
                      <>
                        <CopyButton text={segment.text} />
                        <TranslateButton
                          isTranslating={translatingSegmentIndex === index}
                          onClick={(e) => handleTranslateSegment(index, e)}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onFlashcardRegister(index)
                          }}
                          className="flex items-center justify-center p-0! w-5 h-5 rounded transition-colors hover:bg-accent"
                          title="単語帳に登録"
                        >
                          <BookMarked
                            className={`w-4 h-4 ${isSegmentRegistered(episodeId, index) ? 'text-yellow-500' : 'text-muted-foreground'}`}
                          />
                        </button>
                      </>
                    )}
                    <label className="flex items-center gap-1 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={segment.skipped || false}
                        onChange={(e) => toggleSkipSegment(index, e)}
                        className="cursor-pointer"
                      />
                      <span className="text-xs text-muted-foreground">Skip</span>
                    </label>
                  </div>
                </div>

                {showText === SHOW_TEXT_MODES.FULL && (
                  <>
                    <p className={`text-base ${currentSegmentIndex === index ? 'font-semibold' : ''}`}>
                      {segment.text}
                    </p>

                    {/* 単語レベル表示 */}
                    {segment.words && segment.words.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {segment.words.map((word, wordIndex) => (
                          <span
                            key={wordIndex}
                            onClick={(e) => handleWordClick(word, index, e)}
                            onTouchStart={() => handleWordTouchStart(word, wordIndex, index)}
                            onTouchEnd={handleWordTouchEnd}
                            className="text-xs px-2 py-1 rounded cursor-pointer transition-colors bg-secondary hover:bg-secondary/80"
                            title={`${formatTime(word.start)} - ${formatTime(word.end)}`}
                          >
                            {word.word}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              {/* ディバイダー */}
              {index < transcription.segments.length - 1 && <div className="border-b border-border my-2" />}
            </div>
          </SwipeableListItem>
        ))}
      </SwipeableList>
      {/* 単語ロングタップメニュー */}
      {longPressedWord && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setLongPressedWord(null)}
        >
          <div className="bg-card p-6 rounded-lg shadow-lg max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">word edit</h3>
              <Button onClick={() => setLongPressedWord(null)} variant="ghost">
                ×
              </Button>
            </div>

            <div className="mb-4">
              <input
                type="text"
                value={editingWordText}
                onChange={(e) => setEditingWordText(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!isEditingWord}
                autoFocus={isEditingWord}
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (isEditingWord) {
                    handleEditWord()
                    setLongPressedWord(null)
                  } else {
                    setIsEditingWord(true)
                  }
                }}
                className="flex-1"
              >
                {isEditingWord ? 'save' : 'edit'}
              </Button>
              <Button onClick={handleSplitSegment} variant="outline" className="flex-1">
                split text
              </Button>
              <Button onClick={handleSplitSegment} variant="outline" className="flex-1">
                translate
              </Button>
            </div>
          </div>
        </div>
      )}{' '}
    </div>
  )
}
